// Must import these to be able to export them.
/* eslint-disable @typescript-eslint/no-restricted-imports */
import { strict as assert } from 'assert';
import * as R from 'remeda';

export { assert, R };

// TODO: Rename Freeze, Mutable and mut to all nicely fit together.
/**
 * Make T deeply readonly.
 */
export type Freeze<T> = {
  // If `T` is a primitive type, TypeScript immediately returns it because it cannot
  // be iterated over - it has no keys.
  readonly [K in keyof T]: T[K] extends (...args: never) => unknown // function
    ? T[K]
    : Freeze<T[K]>; // tuple, array, object
};

export type Mutable<T> = {
  -readonly [K in keyof T]: T[K] extends (...args: never) => unknown // function
    ? T[K]
    : Mutable<T[K]>; // tuple, array, object
};

// Without type parameter U it doesn't work.
// eslint-disable-next-line @typescript-eslint/no-unnecessary-type-parameters
export function mut<T, U extends Mutable<T>>(v: Freeze<T>): U {
  return R.clone(v) as U;
}

/**
 * Exists to enforce some structure when returning errors.
 *
 * Message is required to make it the most accurate by defining it in the place
 * with the most context - where it's returned.
 */
export type ErrorBase<TType extends string, TContext = undefined> = {
  type: TType;
  message: string;
} & (TContext extends undefined ? { data?: undefined } : { data: TContext });

export class RaiseError extends Error {
  constructor(message: string) {
    super(`Raised issue: ${message}`);
    this.name = 'RaiseError';
    Object.setPrototypeOf(this, RaiseError.prototype);
  }
}

/**
 * Use in code paths that shouldn't ever happen.
 *
 * You must always provide a message.
 *
 * @throws RaiseError
 */
export function raise<T extends string>(message: Exclude<T, ''>): never {
  assert(arguments.length === 1);
  assert(typeof message === 'string');
  assert(message.length > 0);
  // The point of this function is to throw RaiseError.
  // eslint-disable-next-line no-restricted-syntax
  throw new RaiseError(message);
}

/**
 * Enclose code in an inline block to separate logic and avoid variable name
 * collisions.
 */
export function enclose<T>(fn: () => T) {
  assert(arguments.length === 1);
  assert(typeof fn === 'function');
  // Assert function has 0 arguments.
  assert(fn.length === 0);
  return fn();
}

/**
 * Just returns it's argument which must be provided. Useful for instances of writing
 * `(v) => v` and similar to be more readable.
 *
 * Shouldn't substitute Remeda's `identity` in their context.
 */
export function pass<T>(v: T): T {
  assert.equal(arguments.length, 1);
  return v;
}

/**
 * A function equivalent of `throw` to be able to throw exceptions inline.
 */
export function fail(e: Error): never {
  assert.equal(arguments.length, 1);
  assert(e instanceof Error);
  throw e;
}

/**
 * Converts any value to `Error`.
 */
export function toError(e: unknown): Error {
  if (e instanceof Error) {
    return e;
  } else {
    return new Error('Non-error instance', { cause: e });
  }
}

type ShouldAppendPipe<T> = T extends string | number | boolean | unknown[]
  ? false
  : true;
export type Pipe<T, TThis = T> = <R>(this: TThis, fn: (v: T) => R) => Piped<R>;
type Piped<T> =
  ShouldAppendPipe<T> extends true ? { value: T; pipe: Pipe<T, void> } : T;

/**
 * Appends pipe for values that don't have pipe in prototype, otherwise returns
 * the value.
 *
 * @inner
 */
export function appendPipe<T>(value: T): Piped<T> {
  if (
    typeof value === 'string' ||
    typeof value === 'number' ||
    typeof value === 'boolean' ||
    Array.isArray(value)
  ) {
    return value as Piped<T>;
  } else {
    return {
      value,
      pipe: <R>(fn: (v: T) => R) => appendPipe(fn(value)),
    } as Piped<T>;
  }
}

/**
 * Creates a pipeable object.
 */
export function toPipe<T>(
  value: ShouldAppendPipe<T> extends true ? T : 'Use prototype pipe',
): Piped<T> {
  return appendPipe(value as T);
}
